import { Checkbox, type CheckboxProps } from "expo-checkbox";
import type { ReactNode } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { BaseText, BodyText } from "~/components/layout/text";
import { ErrorMessage } from "~/components/layout/text/ErrorMessage";
import { FieldHint } from "~/components/layout/text/FieldHint";
import { Only } from "~/components/permission/Only";
import { QendoIcon, type QendoIconSet } from "~/components/QendoIcon";
import { Space } from "~/constants/sizing";
import { NoName, useErrorBag } from "~/providers/ErrorBagProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";

type CheckboxInputProps = {
    label: ReactNode;
    icon?: QendoIconSet;
    required?: boolean;
    name?: string;
    error?: string | null;
    hint?: ReactNode;
} & Omit<CheckboxProps, "color">;

const ICON_SIZE = Space.md;

export function CheckboxInput({
    label,
    disabled,
    onValueChange,
    value,
    icon,
    required,
    error,
    hint,
    name = NoName,
    ...checkboxProps
}: CheckboxInputProps) {
    const errorBag = useErrorBag();
    const { colours } = useTheme();
    const field_error = error ?? errorBag.first(name);

    const onChange = (value: boolean) => {
        errorBag.forget(name);
        onValueChange?.(value);
    };

    return (
        <View style={styles.container}>
            <Pressable style={styles.pressable} onPress={() => onChange(!value)} disabled={disabled}>
                <Checkbox
                    {...checkboxProps}
                    value={value}
                    onValueChange={onChange}
                    style={[styles.checkbox, { backgroundColor: colours.check.background }]}
                    color={
                        (disabled && colours.check.disabled) ||
                        (value && colours.check.selector) ||
                        colours.input.border
                    }
                />
                <BodyText size="md" style={styles.label}>
                    {label}
                    {required && (
                        <BaseText size="md" colour="secondary">
                            {" "}
                            (required)
                        </BaseText>
                    )}
                </BodyText>
                {filled(icon) && (
                    <QendoIcon name={icon} color={colours.icon.secondary} size={ICON_SIZE} style={styles.icon} />
                )}
            </Pressable>
            <Only when={hint}>
                <FieldHint>{hint}</FieldHint>
            </Only>
            <Only when={field_error}>
                <ErrorMessage>{field_error}</ErrorMessage>
            </Only>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        alignItems: "flex-start",
    },
    pressable: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: Space.sm,
    },
    checkbox: {
        width: ICON_SIZE,
        height: ICON_SIZE,
        flexShrink: 0,
    },
    label: {
        flexShrink: 1,
    },
    icon: {
        flexShrink: 0,
    },
});
